import { SpeakButton } from '@web/lib/audio'
import type { ExerciseRendererProps } from './types'

export function MultipleChoiceExercise({
  content,
  response,
  onChange,
  locked,
}: ExerciseRendererProps<'multiple_choice'>) {
  const chosen = response?.optionId ?? null

  return (
    <fieldset className="space-y-2.5">
      <legend className="sr-only">Choose one answer</legend>
      {content.options.map((option) => {
        const isChosen = chosen === option.id
        const isCorrect = option.id === content.correctId

        // Once checked, reveal the right answer alongside her choice — seeing
        // both together is what makes a wrong answer teach something.
        let tone = 'ring-sand-200 bg-white'
        if (locked && isCorrect) tone = 'ring-brand-500 bg-brand-50'
        else if (locked && isChosen) tone = 'ring-clay-300 bg-clay-50'
        else if (isChosen) tone = 'ring-brand-600 bg-brand-50 ring-2'

        return (
          <div
            key={option.id}
            className={`flex min-h-13 w-full items-center gap-2 rounded-xl ring-1 transition-colors ${tone}`}
          >
          <button
            type="button"
            disabled={locked}
            aria-pressed={isChosen}
            onClick={() => onChange({ type: 'multiple_choice', optionId: option.id })}
            className="flex min-h-13 flex-1 items-center gap-3 rounded-xl px-4 py-3 text-left text-base disabled:cursor-default"
          >
            <span className="flex-1">{option.text}</span>
            {locked && isCorrect && (
              <span aria-hidden="true" className="text-brand-600">
                ✓
              </span>
            )}
            {locked && isChosen && !isCorrect && (
              <span aria-hidden="true" className="text-clay-500">
                ✗
              </span>
            )}
          </button>
          {/* Sibling, not nested: a button inside a button is invalid HTML and
              breaks keyboard navigation. Options are already on screen, so
              hearing one gives nothing away. */}
          <SpeakButton text={option.text} size="sm" className="mr-2" />
          </div>
        )
      })}
      {/* The explanation is rendered once, by the player's sticky feedback
          panel — that is where she is already looking after checking. */}
    </fieldset>
  )
}
